import { useEffect, useRef, useState, type ReactNode } from "react";
import { CaretDoubleLeftIcon } from "@phosphor-icons/react/CaretDoubleLeft";
import { LockSimpleIcon } from "@phosphor-icons/react/LockSimple";
import { Icon } from "../../Icons/Icon";

/**
 * The off-screen affordance that lets the user restore a dialog that's been
 * pushed aside. Two visual variants:
 *
 *   - `parked` (tray-managed): icon-only chip that smoothly expands its
 *     label on hover via the same inline-grid + max-width pattern Hubble's
 *     auto-collapsing buttons use. Stacked vertically by `topCss` derived
 *     from the entry's parkedIndex. Z-index is fixed low (30) so a freshly
 *     opened modal renders above it. When `closing` is set, the chip slides
 *     out to the right via `transform: translateX(100%)`; consumers
 *     stagger the `closingDelay` to produce a cascade (e.g. Clear-all).
 *
 *   - peek (legacy, page-mounted lookup-only): full-width chip with the
 *     title visible, anchored at top. Z-index follows the dialog's own
 *     contentZ since only one peek can be active per dialog.
 *
 * Lives in its own file to keep DialogInner.tsx under the 400-line cap.
 */
// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function DialogSlideAsideChip_({
  parked,
  isDirty,
  closing,
  closingDelay,
  topCss,
  zIndex,
  titleAttr,
  label,
  onRestore,
  disabledForGrouping = false,
  groupExpanded = false,
  onMouseEnter,
  onMouseLeave,
}: {
  parked: boolean;
  isDirty: boolean;
  closing: boolean;
  closingDelay: number;
  topCss: string;
  zIndex: number;
  titleAttr: string | undefined;
  label: ReactNode;
  onRestore: () => void;
  /** True when this parked chip belongs to a group whose origin route
   *  is not the current route (ENG-4836 POC). The chip is still
   *  rendered (it's needed for grouping math and to signal that work
   *  is parked there) but visually de-emphasised and non-clickable —
   *  the user is expected to click the "← Back to X" group header
   *  above it to navigate back first. Pointer events stay enabled so
   *  hovering still drives the group's expand/collapse animation. */
  disabledForGrouping?: boolean;
  /** True when this chip's group is currently the hovered one. When
   *  `disabledForGrouping`, this drives the chip's label expansion so
   *  the operator can see what they parked in this group — every chip
   *  in the group reveals its label simultaneously while the group is
   *  expanded, then snaps shut when the cursor leaves. */
  groupExpanded?: boolean;
  /** Forwarded directly to the button. Used by the host to keep the
   *  chip's group expanded while the cursor is over it (so the user
   *  can read what they parked). Undefined for chips in the active
   *  group — they're always expanded. Explicit `| undefined` so callers
   *  can pass the ternary result without falling foul of
   *  `exactOptionalPropertyTypes`. */
  onMouseEnter?: (() => void) | undefined;
  onMouseLeave?: (() => void) | undefined;
}) {
  // Click-feedback state for the disabledForGrouping case. Bumping shakeKey
  // is how we (a) reposition the padlock to the chip's current left edge
  // and (b) retrigger the shake animation. State only matters when
  // disabledForGrouping; the chip is otherwise stateless.
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const chipRef = useRef<HTMLButtonElement>(null);
  const [shakeKey, setShakeKey] = useState(0);
  // Chip width is measured at click time so the padlock can hug the chip's
  // actual left edge — the chip expands/shrinks with groupExpanded, so a
  // static right-offset would float adrift whenever the chip wasn't fully
  // expanded.
  const [chipWidthRem, setChipWidthRem] = useState(0);

  function handleDisabledClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (chipRef.current) {
      setChipWidthRem(chipRef.current.getBoundingClientRect().width / 16);
    }
    setShakeKey((k) => k + 1);
  }

  // Re-trigger the shake animation imperatively via classList instead of
  // bumping a React `key` on the button itself. Keying the button remounts
  // it, which fires mouseLeave on the old node and never fires mouseEnter
  // on the new one (the cursor was already inside; browsers only fire
  // mouseenter on entry into bounds). That caused the chip to collapse
  // out of its expanded state on every click. The classList toggle keeps
  // the same DOM node, preserving hover state, and the offsetWidth read
  // forces a reflow so the browser restarts the keyframe from frame 0.
  useEffect(() => {
    if (shakeKey === 0 || !chipRef.current) return;
    const btn = chipRef.current;
    btn.classList.remove("animate-parked-shake");
    // Force reflow — without this the browser coalesces remove+add and the
    // animation never restarts. `void` discards the read so linters don't
    // flag it as a useless expression.
    void btn.offsetWidth;
    btn.classList.add("animate-parked-shake");
  }, [shakeKey]);

  // Parked uses the auto-collapse button pattern: outer is content-sized
  // (no max-width transition on the chip itself), the label span owns the
  // expansion via `inline-grid max-w-0 → group-hover:max-w-48`. Outer
  // `overflow-hidden` clips the label while it's collapsed.
  const baseClass = parked
    ? disabledForGrouping
      ? "group fixed right-0 z-30 flex h-9 items-center overflow-hidden rounded-l-xl border-2 border-r-0 border-border/60 bg-card/60 pr-3 pl-3 text-sm font-medium text-content-faint shadow-md transition-[transform,top] duration-200 ease-out"
      : "group fixed right-0 z-30 flex h-9 items-center overflow-hidden rounded-l-xl border-2 border-r-0 border-border bg-card pr-3 pl-3 text-sm font-medium text-content-secondary shadow-lg transition-[transform,color,background-color,top] duration-200 ease-out hover:bg-muted hover:text-content"
    : "fixed right-0 flex items-center gap-2 rounded-l-xl border-2 border-r-0 border-border bg-card py-3 pr-4 pl-3 text-sm font-medium text-content-secondary shadow-lg transition-colors hover:bg-muted hover:text-content";

  // disabledForGrouping chips need click feedback. Without it, clicking does
  // nothing (the chip is dimmed and onClick was undefined) and the operator
  // can't tell if the chip is alive or dead. We keep the click on the chip
  // itself (rather than wrapping in a separate node) because the chip is
  // position:fixed — any wrapper would have zero size and the padlock
  // anchored to it would float to the wrong place in the document. The
  // padlock is rendered as a sibling fixed element, positioned dynamically
  // against the chip's measured width (chip width varies with hover state).
  return (
    <>
      <button
        ref={chipRef}
        type="button"
        data-testid={parked ? "dialog-tray-chip" : undefined}
        data-disabled-for-grouping={disabledForGrouping || undefined}
        aria-disabled={disabledForGrouping || undefined}
        onClick={disabledForGrouping ? handleDisabledClick : onRestore}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        title={titleAttr}
        className={baseClass}
        style={{
          top: topCss,
          zIndex,
          ...(closing
            ? {
                transform: "translateX(100%)",
                transitionDelay: `${closingDelay}ms`,
                pointerEvents: "none",
              }
            : disabledForGrouping
              ? { cursor: "default" }
              : {}),
        }}
      >
        <span className="relative flex shrink-0 items-center">
          <CaretDoubleLeftIcon className="h-4 w-4" weight="bold" />
          {parked && isDirty && (
            <span
              data-testid="dialog-tray-chip-dirty-dot"
              aria-label="Unsaved changes"
              className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-orange ring-2 ring-card"
            />
          )}
        </span>
        {parked ? (
          disabledForGrouping ? (
            // Disabled chip: label tracks the group's expansion state.
            // When anyone hovers anywhere in the group, every chip's
            // label opens at once so the operator can scan all the
            // parked work in that group without needing to mouse over
            // each chip individually.
            <span
              className={`inline-grid transition-[max-width,margin] duration-300 ease-in-out ${
                groupExpanded ? "ml-2 max-w-48" : "max-w-0"
              }`}
            >
              <span className="overflow-hidden whitespace-nowrap">{label}</span>
            </span>
          ) : (
            <span className="inline-grid max-w-0 transition-[max-width,margin] duration-300 ease-in-out group-hover:ml-2 group-hover:max-w-48">
              <span className="overflow-hidden whitespace-nowrap">{label}</span>
            </span>
          )
        ) : (
          <span className="max-w-48 truncate">{label}</span>
        )}
      </button>
      {disabledForGrouping && shakeKey > 0 && (
        <span
          key={`padlock-${shakeKey}`}
          aria-hidden="true"
          className="pointer-events-none fixed z-30 inline-flex h-9 -translate-y-1/2 animate-parked-padlock items-center text-red"
          // `right` = chip width + 0.5rem gap → padlock sits flush to the
          // left of the chip regardless of expansion state. `marginTop`
          // (half chip height) combined with `-translate-y-1/2` lands the
          // padlock's vertical center on the chip's center line.
          style={{
            top: topCss,
            right: `calc(${chipWidthRem}rem + 0.5rem)`,
            marginTop: "1.125rem",
          }}
        >
          <Icon icon={LockSimpleIcon} size="md" weight="fill" />
        </span>
      )}
    </>
  );
}
